/*gtk*/
/*=============================================================================
  KILLER ACID — DARK MODE
  Visitors get the theme's "Default appearance" setting (light normally, dark
  for seasonal campaigns), which the inline head script in layout/theme.liquid
  writes to <html data-dark-mode-default>. Using the toggle stores a choice in
  localStorage, together with the default that was in force at the time
  (SEASON_KEY). A choice only counts while that default is still the setting:
  when the store flips the default for a campaign, every earlier choice is
  ignored and everyone gets the new default. The toggle still sticks within a
  season. The head script applies the same rule before first paint. System
  preference is deliberately NOT followed — the catalogue is
  illustration-heavy and the artwork should only land on a dark ground because
  the store or the visitor chose it.

  Venue uses an inverted modifier convention: `modal--dark` means "dark ink,
  for a light surface". The theme already ships a complete light-ink variant
  of every drawer, modal and sidebar, so dark mode swaps the modifier rather
  than restyling ~92 rules by hand. See MODIFIER_PAIRS below.
=============================================================================*/

(function () {
  'use strict';

  var STORAGE_KEY = 'killeracid-dark-mode';
  var SEASON_KEY = 'killeracid-dark-mode-season';
  var HINT_KEY = 'killeracid-dark-mode-hint';
  var DARK_CLASS = 'dark-mode';
  var DATA_ATTR = 'data-theme';

  /* Settings passed as data attributes on this file's <script> tag in
     layout/theme.liquid (Theme settings → Dark mode → First-visit hint).
     currentScript is only set while the file first runs, so read it now. */
  var script = document.currentScript;
  var HINT = {
    enabled: !script || script.getAttribute('data-hint') !== 'false',
    toDark: (script && script.getAttribute('data-hint-to-dark')) || 'Try lights out 🌙',
    toLight: (script && script.getAttribute('data-hint-to-light')) || 'Too spooky? Lights on ☀️'
  };

  /* Venue's inverted modifiers: [darkInkClass, lightInkClass].
     In dark mode every surface flips to the light-ink variant. */
  var MODIFIER_PAIRS = [
    ['modal--dark', 'modal--light'],
    ['collection-sidebar--dark', 'collection-sidebar--light'],
    ['footer--dark', 'footer--light']
  ];

  var scheduled = false;

  function defaultTheme() {
    return document.documentElement.getAttribute('data-dark-mode-default') === 'dark'
      ? 'dark'
      : 'light';
  }

  function isDark() {
    return document.documentElement.classList.contains(DARK_CLASS);
  }

  /* Flip Venue's light/dark surface modifiers to match the current mode.

     This is NOT a symmetric swap. Surfaces do not all ship on the same side:
     the drawers ship as modal--dark (dark ink, light drawer) while the footer
     ships as footer--light (light ink, #222 ground). Dark mode wants light ink
     everywhere, but light mode has to restore whatever the theme editor
     actually chose — blindly inverting turns the footer into dark ink on a
     dark ground. So each element records the modifier it arrived with, once,
     and that is what light mode puts back. */
  function syncModifiers() {
    var dark = isDark();

    MODIFIER_PAIRS.forEach(function (pair) {
      var darkInk = pair[0];
      var lightInk = pair[1];

      /* 'modal--dark' -> 'gtkModModal', 'collection-sidebar--dark' -> 'gtkModCollectionSidebar' */
      var key =
        'gtkMod' +
        darkInk.split('--')[0].replace(/(^|-)([a-z])/g, function (m, dash, c) {
          return c.toUpperCase();
        });

      Array.prototype.forEach.call(
        document.querySelectorAll('.' + darkInk + ', .' + lightInk),
        function (el) {
          if (el.dataset[key] === undefined) {
            el.dataset[key] = el.classList.contains(darkInk) ? darkInk : lightInk;
          }

          var want = dark ? lightInk : el.dataset[key];
          el.classList.remove(want === darkInk ? lightInk : darkInk);
          el.classList.add(want);
        }
      );
    });
  }


  /* Lazily-injected surfaces (quick-shop modals, re-rendered cart contents)
     arrive after the initial sync, so keep an eye on the tree while dark.

     setTimeout rather than requestAnimationFrame: rAF does not fire at all in
     a background tab, so a store opened in one (middle-clicked from a
     collection, say) would leave late-injected drawers un-swapped until the
     visitor focused it. */
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      syncModifiers();
    }, 0);
  }

  function apply(dark) {
    var root = document.documentElement;

    root.classList.toggle(DARK_CLASS, dark);
    root.setAttribute(DATA_ATTR, dark ? 'dark' : 'light');
    /* Lets form controls, scrollbars and browser chrome follow along. */
    root.style.colorScheme = dark ? 'dark' : 'light';

    syncModifiers();

    Array.prototype.forEach.call(
      document.querySelectorAll('.dark-mode-toggle'),
      function (toggle) {
        /* The menu switch keeps its visible "Dark mode" label as its name and
           reports on/off; icon buttons get a label saying what a click does. */
        if (toggle.getAttribute('role') === 'switch') {
          toggle.setAttribute('aria-checked', String(dark));
          return;
        }
        toggle.setAttribute('aria-pressed', String(dark));
        toggle.setAttribute(
          'aria-label',
          dark ? 'Switch to light mode' : 'Switch to dark mode'
        );
      }
    );

    window.dispatchEvent(
      new CustomEvent('themechange', { detail: { theme: dark ? 'dark' : 'light' } })
    );
  }

  /* Remember a choice along with the default it was made under. */
  function save(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      localStorage.setItem(SEASON_KEY, defaultTheme());
    } catch (e) {
      /* Private browsing — the choice just won't survive the page. */
    }
  }

  function forget() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SEASON_KEY);
    } catch (e) {}
  }

  /* Shopify customer event. Nothing reads it until a custom pixel subscribes
     (Settings → Customer events): analytics.subscribe('dark_mode_toggle', …). */
  function track(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data);
      }
    } catch (e) {}
  }

  /* source: which control was used — header, header-mobile, footer, drawer
     (the toggle snippet's variant) or 'api'. */
  function toggle(source) {
    var dark = !isDark();
    save(dark ? 'dark' : 'light');
    apply(dark);
    track('dark_mode_toggle', {
      mode: dark ? 'dark' : 'light',
      source: source || 'api',
      default_appearance: defaultTheme(),
      page: window.location.pathname
    });
  }

  /* -- First-visit hint ------------------------------------------------------
     Points the header toggle out once per "season" (per Default appearance
     value): the icon glows and wiggles and a small bubble appears beneath it
     for a few seconds. Skipped for anyone who has already used the toggle this
     season. It never stacks on another overlay: it waits until the centre of
     the screen is clear of any fixed-position layer — the Klaviyo signup
     popup, the menu, the cart popup — without knowing which app it is, and
     steps aside if one opens while it is showing. */
  var hint = null;
  var hintUsed = false; /* At most one hint per page, whatever happens. */

  /* Two independent signals, so no single app's markup has to be known:
     1. an open dialog announced as modal (aria-modal="true") — accessible
        popups such as Klaviyo's forms and the theme's own drawers;
     2. anything in a fixed-position layer covering the middle of the screen. */
  function overlayOpen() {
    var dialogs = document.querySelectorAll('[aria-modal="true"]');
    for (var i = 0; i < dialogs.length; i++) {
      var r = dialogs[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && !dialogs[i].closest('[aria-hidden="true"]')) return true;
    }
    var el = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
    for (var p = el; p && p !== document.documentElement; p = p.parentElement) {
      if (window.getComputedStyle(p).position === 'fixed') return true;
    }
    return false;
  }

  /* The header icon currently on screen (desktop nav or mobile trigger). */
  function hintTarget() {
    var candidates = document.querySelectorAll('.dark-mode-toggle--header, .dark-mode-toggle--header-mobile');
    for (var i = 0; i < candidates.length; i++) {
      var r = candidates[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && r.top >= 0 && r.bottom <= window.innerHeight) {
        return candidates[i];
      }
    }
    return null;
  }

  function hintSeen() {
    try {
      return localStorage.getItem(HINT_KEY) === defaultTheme();
    } catch (e) {
      return true; /* No storage: don't nag on every page. */
    }
  }

  function chosenThisSeason() {
    try {
      return !!localStorage.getItem(STORAGE_KEY) && localStorage.getItem(SEASON_KEY) === defaultTheme();
    } catch (e) {
      return false;
    }
  }

  /* seen: false only when an overlay interrupted the hint before anyone had
     a chance to read it, so it gets another go on a later page. */
  function hideHint(seen) {
    if (!hint) return;
    var h = hint;
    hint = null;
    if (seen) {
      try {
        localStorage.setItem(HINT_KEY, defaultTheme());
      } catch (e) {}
    }
    clearTimeout(h.timer);
    clearInterval(h.watch);
    h.cleanup();
    h.target.classList.remove('dark-mode-toggle--nudge');
    h.bubble.classList.remove('is-visible');
    setTimeout(function () {
      if (h.bubble.parentNode) h.bubble.parentNode.removeChild(h.bubble);
    }, 250);
  }

  function showHint(target) {
    if (hintUsed) return;
    hintUsed = true;

    /* Clear any stray bubble so one can never be left behind. */
    Array.prototype.forEach.call(document.querySelectorAll('.dark-mode-hint'), function (old) {
      if (old.parentNode) old.parentNode.removeChild(old);
    });

    var bubble = document.createElement('div');
    bubble.className = 'dark-mode-hint';
    bubble.setAttribute('role', 'status');
    bubble.textContent = isDark() ? HINT.toLight : HINT.toDark;
    document.body.appendChild(bubble);

    /* Centre under the icon, kept 16px inside the viewport; the arrow follows
       the icon even when the bubble is pushed in from the edge. */
    var r = target.getBoundingClientRect();
    var width = bubble.offsetWidth;
    var centre = r.left + r.width / 2;
    var left = Math.max(16, Math.min(centre - width / 2, window.innerWidth - 16 - width));
    bubble.style.left = left + window.scrollX + 'px';
    bubble.style.top = r.bottom + window.scrollY + 10 + 'px';
    bubble.style.setProperty('--arrow-x', centre - left + 'px');

    target.classList.add('dark-mode-toggle--nudge');
    setTimeout(function () {
      bubble.classList.add('is-visible');
    }, 20);

    var shownAt = Date.now();
    var dismiss = function () {
      hideHint(true);
    };
    /* Several independent ways out, so no single event that a browser or the
       theme swallows can leave the bubble stuck: any press, click, key, scroll
       (window, wheel or touch-drag — some pages scroll an inner element), or
       leaving the page. Clicking the bubble itself counts too. */
    var DISMISS_EVENTS = ['pointerdown', 'click', 'keydown', 'wheel', 'touchmove'];
    window.addEventListener('scroll', dismiss, { passive: true });
    window.addEventListener('pagehide', dismiss);
    DISMISS_EVENTS.forEach(function (type) {
      document.addEventListener(type, dismiss, { capture: true, passive: true });
    });

    hint = {
      target: target,
      bubble: bubble,
      cleanup: function () {
        window.removeEventListener('scroll', dismiss, { passive: true });
        window.removeEventListener('pagehide', dismiss);
        DISMISS_EVENTS.forEach(function (type) {
          document.removeEventListener(type, dismiss, { capture: true, passive: true });
        });
      },
      timer: setTimeout(dismiss, 6000),
      watch: setInterval(function () {
        if (overlayOpen()) hideHint(Date.now() - shownAt > 2000);
      }, 500)
    };

    track('dark_mode_hint_shown', {
      mode: isDark() ? 'dark' : 'light',
      default_appearance: defaultTheme(),
      page: window.location.pathname
    });
  }

  /* Starts 3s after load, then needs the icon on screen and no overlay for two
     checks in a row. Gives up after 45s and tries again on the next page. */
  function scheduleHint() {
    if (!HINT.enabled || hintSeen() || chosenThisSeason()) return;
    if (window.Shopify && Shopify.designMode) return; /* Not in the theme editor. */

    var clearChecks = 0;
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      if (tries > 45 || chosenThisSeason()) {
        clearInterval(poll);
        return;
      }
      if (document.visibilityState !== 'visible') {
        clearChecks = 0;
        return;
      }
      var target = hintTarget();
      if (!target || overlayOpen()) {
        clearChecks = 0;
        return;
      }
      clearChecks += 1;
      if (clearChecks >= 2) {
        clearInterval(poll);
        showHint(target);
      }
    }, 1000);
  }

  function init() {
    /* A choice made under an earlier default has already been ignored by the
       head script; drop it so it can't resurface if the default flips back. */
    try {
      if (localStorage.getItem(STORAGE_KEY) &&
          localStorage.getItem(SEASON_KEY) !== defaultTheme()) {
        forget();
      }
    } catch (e) {}

    /* The inline head script has already set the class to avoid a flash;
       this re-runs apply() so aria state and surface modifiers catch up. */
    apply(isDark());

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.dark-mode-toggle');
      if (!trigger) return;
      e.preventDefault();
      hideHint(true); /* Using the toggle is the hint's job done. */
      var variant = /dark-mode-toggle--([a-z-]+)/.exec(trigger.className);
      toggle(variant ? variant[1] : 'unknown');
    });

    var startHint = function () {
      setTimeout(scheduleHint, 3000);
    };
    if (document.readyState === 'complete') {
      startHint();
    } else {
      window.addEventListener('load', startHint);
    }

    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (isDark()) scheduleSync();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.KillerAcidDarkMode = {
    toggle: function () {
      toggle('api');
    },
    isDark: isDark,
    set: function (theme) {
      save(theme);
      apply(theme === 'dark');
    },
    get: function () {
      return isDark() ? 'dark' : 'light';
    },
    /* Forget the visitor's choice and fall back to the theme default. */
    clear: function () {
      forget();
      apply(defaultTheme() === 'dark');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
